import { Field, type FieldProps } from '@upright/ui/common/generic/components/field';
import TaillessLineArrowDown1 from '@upright/ui/icons/img/streamline/tailless-line-arrow-down-1.svg';
import TaillessLineArrowUp1 from '@upright/ui/icons/img/streamline/tailless-line-arrow-up-1.svg';
import { Select as AriaSelect, SelectProps as AriaSelectProps, SelectValue } from 'react-aria-components';
import { Button, type ButtonInternalProps } from '../../button';
import { ClearTextSlots, Text } from '../../typography';
import { ListBox, type Props as ListBoxProps } from '../ListBox';
import commonStyles from '../Dropdowns.module.css';

type Props<T extends object, M extends 'single' | 'multiple'> = Pick<
  AriaSelectProps<T, M>,
  | 'children'
  | 'value'
  | 'defaultValue'
  | 'isOpen'
  | 'defaultOpen'
  | 'onChange'
  | 'onBlur'
  | 'isDisabled'
  | 'onOpenChange'
  | 'selectionMode'
  | 'placeholder'
> &
  FieldProps &
  ListBoxProps<T> &
  Pick<ButtonInternalProps, 'size' | 'variant' | 'color' | 'startIcon'> & {
    shouldMatchTriggerWidth?: boolean;
    /**
     * Shows the label inside the trigger, before the value ("Type: Default mode"), so a row of
     * filters stays one line. The field label is then visually hidden but still names the select.
     */
    isLabelInline?: boolean;
  };

export function Select<T extends object, M extends 'single' | 'multiple' = 'single'>({
  isOpen: isOpenFromProps,
  defaultOpen,
  onOpenChange,
  selectionMode,
  // value/defaultValue/onChange must be forwarded to AriaSelect, otherwise the
  // dropdown is uncontrolled and the consumer's onChange never fires — the
  // caller's form state silently keeps its default value (PLT-5569).
  value,
  defaultValue,
  onChange,
  onBlur,
  placeholder,

  label,
  isLabelVisuallyHidden,
  isLabelInline,
  description,
  error,

  size,
  variant,
  color,

  placement,
  isDisabled,
  shouldMatchTriggerWidth,
  children,
  items,
}: Props<T, M>) {
  return (
    <div className={commonStyles.wrapper}>
      <AriaSelect
        isOpen={isOpenFromProps}
        isDisabled={isDisabled}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        selectionMode={selectionMode}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
      >
        {({ isOpen }) => (
          <Field
            label={label}
            isLabelVisuallyHidden={isLabelVisuallyHidden || isLabelInline}
            description={description}
            error={error}
          >
            <Button
              size={size}
              variant={variant}
              color={error ? 'danger' : color}
              endIcon={isOpen ? TaillessLineArrowUp1 : TaillessLineArrowDown1}
              isDisabled={isDisabled}
            >
              <ClearTextSlots>
                <Text truncate>
                  {/* Outside `SelectValue`, so the accessible name does not repeat the hidden label. */}
                  {isLabelInline ? <span aria-hidden="true">{label}: </span> : null}
                  {/* An option renders as a block, which would drop the value onto a line of its own. */}
                  <SelectValue>
                    {({ defaultChildren, selectedText, isPlaceholder }) =>
                      isLabelInline && !isPlaceholder ? selectedText : defaultChildren
                    }
                  </SelectValue>
                </Text>
              </ClearTextSlots>
            </Button>
            <ListBox placement={placement} shouldMatchTriggerWidth={shouldMatchTriggerWidth} items={items}>
              {children}
            </ListBox>
          </Field>
        )}
      </AriaSelect>
    </div>
  );
}
